import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { GatewayFactory } from '../../payment-gateways/core/components/GatewayFactory';
import { GatewaySelector } from '../../payment-gateways/core/components/GatewaySelector';
import { PaymentErrorScreen } from '../../payment-gateways/core/components/PaymentErrorScreen';
import { getGatewaysForCountry } from '../../payment-gateways/core/registry';
import { buildCallbackPath } from '../../payment-gateways/core/utils/callbackUrl';
import {
  PaymentError,
  PaymentErrorCode,
} from '../../payment-gateways/core/utils/errors';
import type {
  GatewayManifestEntry,
  OrderContext,
} from '../../payment-gateways/core/types';

interface GatewayHostProps {
  order: OrderContext;
  onError: (error: PaymentError) => void;
}

/**
 * Picks the gateway for this order's country and runs it. Everything here
 * is driven by the registry — there is no per-country branching, so a new
 * gateway never touches this file (see SKILLS.md's Recipe 1).
 *
 * Which gateway is selected is this component's own business, so the
 * selection state lives here rather than being lifted into PaymentPage,
 * which has no use for it.
 */
export function GatewayHost({ order, onError }: GatewayHostProps) {
  const navigate = useNavigate();
  const [selected, setSelected] = useState<GatewayManifestEntry | null>(null);
  const gateways = useMemo(
    () => getGatewaysForCountry(order.country),
    [order.country]
  );

  if (gateways.length === 0) {
    return (
      <PaymentErrorScreen
        error={
          new PaymentError(
            PaymentErrorCode.CONFIG_ERROR,
            'No payment method is configured for this region.',
            { country: order.country }
          )
        }
      />
    );
  }

  // A country with exactly one gateway skips the selection screen — no
  // point asking a question with one answer.
  const active = selected ?? (gateways.length === 1 ? gateways[0] : null);

  if (!active) {
    return <GatewaySelector entries={gateways} onSelect={setSelected} />;
  }

  return (
    <GatewayFactory
      entry={active}
      order={order}
      onSettled={(payload) => {
        // An inline-SDK gateway (Razorpay) settles in-page — no real
        // navigation has happened yet, so this is the one hop into
        // /payment/callback, and it's same-origin/same-router: a
        // client-side navigate(), not a window.location redirect.
        // Contrast GatewayFactory's hosted-checkout redirect and
        // bookingStatusRedirect.ts, both of which leave this app entirely
        // and have to stay window.location.href.
        navigate(
          buildCallbackPath({
            gatewayId: active.id,
            orderId: order.orderId,
            status: payload.claimedStatus,
            paymentId: payload.gatewayPaymentId,
          })
        );
      }}
      onError={onError}
    />
  );
}
